import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Button,
  useDisclosure,
  ModalCloseButton,
} from "@chakra-ui/react";
import { clearDependentFields } from "../../../utils/vehicleFieldUtils";
import VehicleSearchForm from "./VehicleSearchForm";
import { SearchListModal } from "@svan-garaaz/reactsharedlib";

interface CarItem {
  id?: number;
  name?: string;
  value: string;
  label: string;
  oemBrandUrl?: string;
  oemUrl?: string;
}

interface ChooseCarModelProps {
  isVisible: boolean;
  onClose: () => void;
  onConfirm: (values: any) => void;
  selectedValues?: Record<string, any>;
  onValueChange: (field: string, value: any) => void;
  onError?: (message: string) => void;
  errorMessage?: string;
  showCancelButton?: boolean;
  confirmButtonText?: string;
  slideUpPanelTitle?: string;
  isConfirmButtonDisabled?: boolean;
  onLpnSearch?: (licensePlateNumber: string) => void;
}

const ChooseCarModel: React.FC<ChooseCarModelProps> = ({
  isVisible,
  onClose,
  onConfirm,
  selectedValues = {},
  onValueChange,
  onError,
  errorMessage,
  showCancelButton = true,
  confirmButtonText = "Confirm",
  slideUpPanelTitle = "Search by Vehicle",
  isConfirmButtonDisabled = false,
  onLpnSearch,
}) => {
  const { isOpen, onOpen, onClose: onModalClose } = useDisclosure();
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [dropdownData, setDropdownData] = useState<CarItem[]>([]);
  const [dropdownTitle, setDropdownTitle] = useState("");

  // Open modal when isVisible changes
  React.useEffect(() => {
    if (isVisible) {
      onOpen();
    } else {
      onModalClose();
    }
  }, [isVisible, onOpen, onModalClose]);

  const handleModalClose = () => {
    onModalClose();
    onClose();
  };

  const handleDropdownOpen = (
    field: string,
    data: CarItem[],
    title: string
  ) => {
    setActiveDropdown(field);
    setDropdownData(data);
    setDropdownTitle(title);
  };

  const handleDropdownClose = () => {
    setActiveDropdown(null);
    setDropdownData([]);
    setDropdownTitle("");
  };

  const handleSelectItem = async (item: CarItem) => {
    if (activeDropdown) {
      clearDependentFields(activeDropdown, onValueChange);
      onValueChange(activeDropdown, item.value);
      onValueChange("oemUrl", item.oemUrl);

      handleDropdownClose();
    }
  };

  const handleConfirm = (values: any) => {
    onConfirm(values);
    handleModalClose();
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleModalClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader>{slideUpPanelTitle}</ModalHeader>
          <ModalBody>
            <VehicleSearchForm
              onCancel={handleModalClose}
              onConfirm={handleConfirm}
              selectedValues={selectedValues}
              onValueChange={onValueChange}
              onDropdownOpen={handleDropdownOpen}
              onError={onError}
              showCancelButton={showCancelButton}
              confirmButtonText={confirmButtonText}
              isConfirmButtonDisabled={isConfirmButtonDisabled}
              errorMessage={errorMessage}
              onLpnSearch={onLpnSearch}
            />

            <SearchListModal
              modalHeading={dropdownTitle}
              options={dropdownData}
              isOpen={!!activeDropdown}
              onClose={handleDropdownClose}
              handleSelectListItem={handleSelectItem}
              displayKey="label"
              searchInputPlaceholder="Search..."
              zeroOptionsMessage="No options available"
            ></SearchListModal>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ChooseCarModel;
